import { useContext, useEffect, useMemo, useState } from 'react';
import { UsePostEditorResult } from './usePostEditorNew';
import {
  GetPostQuery,
  GetPostsEditorQuery,
  PostItemFragment,
  useGetBitlyAccountsQuery,
  useGetPostLazyQuery,
  useGetPostsEditorLazyQuery
} from '../models/graphql';
import { AppContext, isPostEditorContextTypeEditGuard } from '../providers/AppProvider';
import { ApolloError } from '@apollo/client';
import { useCategoriesProfilesWithBoards } from './useCategoriesProfilesWithBoards';
import { PostEditorContextEnum, PostEditorState } from 'models/post-editor';
import { SocialProfileWithBoardsAndMentionType } from 'modules/PostEditor/helpers/mapSelectedProfilesValues';
import { InitialPostEditorState } from '../modules/PostEditor/states/initial';
import { profilesHelpers } from '../modules/Settings/Profiles/Profiles.helpers';
import { MappedCategoriesType } from 'modules/Publish/Categories/Categories.helpers';
import { usePostEditorWorkspaceSettings } from './usePostEditorWorkspaceSettings';
import { ImmutabilityContext } from '../modules/PostEditor/states/effects/customImmutabilityContext';
import { mapFetchedPostsToState } from '../modules/PostEditor/helpers/mapFetchedPostsToState';
import { showToast } from '../helpers/showToast';
import { triggerErrorToast } from '../helpers/triggerErrorToast';
import { useClosePostEditor } from './useClosePostEditor';

export type UsePostEditorPostFetchResult = UsePostEditorResult & {
  fetchedFamily: PostItemFragment[];
};

// Type guard for ensuring data structure matches expected type
const isGetPostsEditorQueryResult = (
  data: GetPostQuery | GetPostsEditorQuery | undefined
): data is GetPostsEditorQuery => !!data && 'latestFamilyMembersOfPost' in data;

const isGetPostQueryResult = (data: GetPostQuery | GetPostsEditorQuery | undefined): data is GetPostQuery =>
  !!data && 'post' in data;

export const usePostEditorPostFetch = (postId: string, context: 'preview' | 'edit'): UsePostEditorPostFetchResult => {
  const { postEditorContext, selectedWorkspaceId } = useContext(AppContext);
  const { closePostEditor } = useClosePostEditor();

  if (context === 'edit' && !isPostEditorContextTypeEditGuard(postEditorContext)) {
    throw new Error(
      'Post Editor Edit has been called with the wrong context value: ' + JSON.stringify(postEditorContext)
    );
  }

  const [getPostQuery] = useGetPostLazyQuery();
  const [getPostEditorQuery] = useGetPostsEditorLazyQuery();
  const [postsError, setPostError] = useState<ApolloError | undefined>(undefined);
  const [fetchedFamily, setFetchedFamily] = useState<PostItemFragment[]>([]);
  const { postEditorCategoriesProfiles, error: categoriesProfilesError } = useCategoriesProfilesWithBoards();
  const [loading, setLoading] = useState<boolean>(true);
  const [initialState, setInitialState] = useState<PostEditorState>(InitialPostEditorState());
  const onClosePostEditor =
    context === 'edit' && isPostEditorContextTypeEditGuard(postEditorContext) ? postEditorContext.onClose : undefined;
  const postEditorContextType =
    context === 'edit' && isPostEditorContextTypeEditGuard(postEditorContext)
      ? postEditorContext.type
      : PostEditorContextEnum.edit;

  const profiles: SocialProfileWithBoardsAndMentionType[] = useMemo(() => {
    const socialProfiles = postEditorCategoriesProfiles?.workspace.socialProfiles || [];
    return socialProfiles.map(profile => ({
      ...profile,
      mentionType: profilesHelpers.getMentionType(profile)
    }));
  }, [postEditorCategoriesProfiles?.workspace.socialProfiles]);

  const categories: MappedCategoriesType[] = useMemo(
    () => postEditorCategoriesProfiles?.workspace.categories || [],
    [postEditorCategoriesProfiles?.workspace.categories]
  );

  const { data: bitlyAccounts, error: bitlyAccountsError } = useGetBitlyAccountsQuery({
    variables: { workspaceId: selectedWorkspaceId }
  });

  const workspaceSettings = usePostEditorWorkspaceSettings();

  useEffect(() => {
    const isRestoringOrRescheduling =
      postEditorContextType === PostEditorContextEnum.restore_trashed ||
      postEditorContextType === PostEditorContextEnum.reschedule_edit;
    const shouldFetchFamily = context === 'edit' && !isRestoringOrRescheduling;
    const fetchQuery = shouldFetchFamily ? getPostEditorQuery : getPostQuery;

    fetchQuery({ variables: { postId } })
      .then(({ data, error }) => {
        if (data) {
          if (isGetPostsEditorQueryResult(data) && shouldFetchFamily) {
            setFetchedFamily(data.latestFamilyMembersOfPost);
          } else if (isGetPostQueryResult(data) && !shouldFetchFamily) {
            setFetchedFamily([data.post]);
          } else {
            throw new Error('Data is missing. it not a result of GetPostQuery nor GetPostsEditorQuery');
          }
        } else if (error) {
          throw error;
        }
      })
      .catch(error => {
        setPostError(error);
      });
  }, [postId, context, getPostEditorQuery, getPostQuery, postEditorContextType]);

  useEffect(() => {
    if (profiles.length && categories.length && fetchedFamily.length && bitlyAccounts && workspaceSettings) {
      try {
        const { update: updateImmutable } = ImmutabilityContext;
        const { evergreen, queuePosition, scheduledTimezone, varyMedia } = workspaceSettings;
        const postGroup = updateImmutable(InitialPostEditorState().postGroups[0], {
          groupPostAttributes: {
            scheduling: {
              evergreen: { active: { $set: evergreen } },
              queuePosition: { $set: queuePosition },
              scheduledTimezone: { $set: scheduledTimezone }
            },
            content: {
              varyMedia: { $set: varyMedia }
            }
          }
        });

        const result = mapFetchedPostsToState(fetchedFamily, postId, postGroup, bitlyAccounts, postEditorContextType);

        if (!fetchedFamily[0].familyUuid) {
          throw Error('Fetched Post missing familyUuid!');
        }

        const resultWithFamilyUuid = updateImmutable(result, {
          familyUuid: { $set: result.familyUuid }
        });

        setLoading(false);
        const initialState = InitialPostEditorState();
        setInitialState(
          updateImmutable<PostEditorState>(initialState, {
            postGroups: {
              0: { $set: resultWithFamilyUuid }
            },
            disconnectedProfiles: {
              $set: profiles.flatMap(profile => {
                if (profile.connected) return [];
                const postFragment = fetchedFamily.find(post => post.socialProfile.id === profile.id);
                return {
                  id: profile.id,
                  shown: false,
                  selected: !!postFragment,
                  __typename: profile.__typename
                };
              })
            }
          })
        );
      } catch (e) {
        showToast({
          severity: 'error',
          summary: 'Error Mapping Post data',
          detail: 'We could not properly map the post data.'
        });

        onClosePostEditor && onClosePostEditor(false);
        closePostEditor();
      }
    }
  }, [
    postEditorContextType,
    profiles,
    categories,
    fetchedFamily,
    postId,
    context,
    closePostEditor,
    bitlyAccounts,
    workspaceSettings,
    onClosePostEditor
  ]);

  useEffect(() => {
    if (postsError) {
      setLoading(false);
      triggerErrorToast('Error Fetching Post')(postsError);
    }
  }, [postsError]);

  useEffect(() => {
    if (categoriesProfilesError) {
      setLoading(false);
      triggerErrorToast('Error Fetching Profiles and Categories')(categoriesProfilesError);
    }
  }, [categoriesProfilesError]);

  useEffect(() => {
    if (bitlyAccountsError) {
      setLoading(false);
      triggerErrorToast('Error Fetching Bitly Accounts')(bitlyAccountsError);
    }
  }, [bitlyAccountsError]);

  return {
    loading,
    profiles,
    categories,
    initialState,
    fetchedFamily,
    error: postsError || categoriesProfilesError || bitlyAccountsError
  };
};
